import { useState } from "react"
import { dateToSlice, getDeviceTimeZone, getImage, getMomentDate, NTformatDate } from "../../../utils"
import moment from "moment"
import { useDispatch, useSelector } from "react-redux"
import { getDayAvailability } from "../../../store/appointment/appointmentActions"
import { FaAngleDown, FaAngleUp } from "react-icons/fa6"
import { WorkHoursCancel } from "./cancel"
import { WorkHoursReschedule } from "./reschedule"

const RangeEventBlocked = ({ currentEvent, events = [], setIsOpen, isMobile = false, isMobileToggled, setIsMobileToggled, isReschedule, setIsReschedule, isCancel, setIsCancel, isBlock, setIsBlock }) => {

    const [availableOptions, setAvailableOptions] = useState([]);
    const { user } = useSelector((state) => state.auth)
    const { currentProfile } = useSelector((state) => state.profile)

    const dispatch = useDispatch();

    const hasTeaching = events.some((e) => e.role === "tutor")
    const hasLearning = events.some((e) => e.role === "student")
    const teachingEvents = events.filter((e) => e.role !== "student")

    const getDayAvailabilityAction = (day) => {
        setAvailableOptions([]);
        dispatch(getDayAvailability({
            profile: currentProfile._id,
            day: NTformatDate(day, user.timezone),
            isSchedule: true
        }))
            .unwrap()
            .then(v => {
                const slices = v.map((slice => {
                    return {
                        value: getMomentDate(slice.date),
                        label: dateToSlice(slice.date)
                    };
                }))

                setAvailableOptions(slices)
            })
    }

    const handleCancelCourse = () => {
        setIsCancel(true)
    }

    const Appointment = ({ event }) => {
        return (
            <div className="flex items-center gap-3 rounded-lg border border-nt-grey-light bg-white px-3 py-2">
                <img className="w-10 h-10 rounded-full object-cover shrink-0" src={getImage(event.avatar, "AVATAR")} alt="" />
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <h4 className="capitalize text-nt-blue font-medium truncate">{event.fullName}</h4>
                        {event.role === "tutor" && (
                            <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-nt-blue/10 text-nt-blue font-semibold uppercase tracking-wide">
                                Teaching
                            </span>
                        )}
                        {event.role === "student" && (
                            <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-nt-grey-light text-nt-grey font-semibold uppercase tracking-wide">
                                Learning
                            </span>
                        )}
                    </div>
                    <span className="text-sm text-nt-grey">
                        {moment(event.date).parseZone().local().format("YYYY-MM-DD HH:mm").toString()}
                    </span>
                </div>
            </div>
        )
    }

    const showActions = !isReschedule && events.length > 0 && !isCancel && !isBlock;

    const conflictDescription = hasLearning
        ? (events.length === 1
            ? "This slot conflicts with a course you teach or a lesson you take as a student."
            : `${events.length} slots conflict with courses you teach or lessons you take as a student.`)
        : (events.length === 1
            ? "This course falls outside your new availability."
            : `${events.length} courses fall outside your new availability.`)

    const blockButtonLabel = hasLearning && !hasTeaching
        ? "Block as tutor"
        : "Block these slots"

    return (
        <div className="flex h-full flex-col rounded-xl border border-nt-grey-light bg-nt-grey-light/40 p-4 md:p-5">
            <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                    <h3 className="text-base font-semibold text-nt-blue">Schedule conflict</h3>
                    <p className="text-sm text-nt-grey mt-1">
                        {conflictDescription}
                        {" "}Choose how to resolve before continuing.
                    </p>
                </div>
                {isMobile ? (
                    <button
                        type="button"
                        className="shrink-0 flex items-center justify-center w-8 h-8 border-2 border-nt-blue rounded-full"
                        onClick={() => setIsMobileToggled((x) => !x)}
                    >
                        {isMobileToggled ? <FaAngleDown /> : <FaAngleUp />}
                    </button>
                ) : null}
            </div>

            {!isReschedule && (
                <div className="space-y-2 mb-5">
                    {events.map((ev) => (
                        <Appointment event={ev} key={ev._id} />
                    ))}
                </div>
            )}

            {showActions && (
                <div className="flex flex-col gap-3 mt-auto">
                    {setIsBlock ? (
                        <button
                            type="button"
                            className="bg-nt-blue text-white px-4 py-3 rounded-lg w-full text-sm font-medium hover:opacity-90 transition"
                            onClick={() => setIsBlock(true)}
                        >
                            {blockButtonLabel}
                        </button>
                    ) : null}
                    {hasTeaching ? (
                        <>
                            <button
                                type="button"
                                className="bg-white border border-nt-blue text-nt-blue px-4 py-3 rounded-lg w-full text-sm font-medium hover:bg-nt-grey-light/50 transition"
                                onClick={() => { setIsReschedule(true); getDayAvailabilityAction(moment().add(1, "day").toDate()) }}
                            >
                                Reschedule courses
                            </button>
                            <button
                                type="button"
                                className="bg-nt-red text-white px-4 py-3 rounded-lg w-full text-sm font-medium hover:opacity-90 transition"
                                onClick={() => handleCancelCourse()}
                            >
                                Cancel courses
                            </button>
                        </>
                    ) : null}
                    <button
                        type="button"
                        className="text-nt-grey px-4 py-2 rounded-lg w-full text-sm hover:text-nt-black transition"
                        onClick={() => setIsOpen(false)}
                    >
                        Dismiss
                    </button>
                </div>
            )}

            {isBlock && (
                <div className="mt-auto rounded-lg border border-nt-blue/30 bg-white px-4 py-3">
                    <p className="text-sm text-nt-grey">
                        {hasLearning
                            ? "Your new weekly schedule will be saved. Time slots will be blocked on your tutor calendar. Courses you teach stay booked; lessons you take as a student are not affected."
                            : "Your new weekly schedule will be saved. The listed time slots will be blocked on your calendar while your existing courses remain booked."}
                    </p>
                </div>
            )}

            {isCancel && (
                <WorkHoursCancel
                    setIsOpen={() => setIsOpen()}
                    setIsCancel={() => setIsCancel()}
                    currentEvent={currentEvent}
                    events={teachingEvents}
                />
            )}
            {isReschedule && (
                <WorkHoursReschedule
                    setIsOpen={(v) => setIsOpen(v)}
                    setIsReschedule={(v) => setIsReschedule(v)}
                    key={events}
                    currentEvent={currentEvent}
                    events={teachingEvents}
                />
            )}
        </div>
    )
}

export { RangeEventBlocked }
